<template>
  <div id="app" class="min-h-screen bg-white">
    <!-- Header -->
    <AppHeader />
    
    <!-- Main Content -->
    <main>
      <router-view />
    </main>
    
    <!-- Loading Spinner -->
    <div v-if="isLoading" class="fixed inset-0 bg-white bg-opacity-75 flex items-center justify-center z-50">
      <div class="spinner"></div>
    </div>
    
    <!-- Toast Notifications -->
    <Teleport to="body">
      <div
        v-if="notification.show"
        :class="[
          'fixed top-4 right-4 px-6 py-4 rounded-lg shadow-lg z-50 transition-all duration-300',
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          notification.type === 'warning' ? 'bg-yellow-500 text-black' :
          'bg-blue-500 text-white'
        ]"
      >
        <div class="flex items-center space-x-3">
          <span>{{ notification.message }}</span>
          <button @click="hideNotification" class="text-white hover:text-gray-200">
            ×
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppHeader from '@/components/AppHeader.vue'
import { useNotificationStore } from '@/stores/notification'
import { useLanguageStore } from '@/stores/language'

export default {
  name: 'App',
  components: {
    AppHeader
  },
  setup() {
    const { locale } = useI18n()
    const router = useRouter()
    const notificationStore = useNotificationStore()
    const languageStore = useLanguageStore()
    const isLoading = ref(false)

    // Reactive notifications
    const notification = ref({
      show: false,
      message: '',
      type: 'info'
    })

    // Hide notification
    const hideNotification = () => {
      notification.value.show = false
    }

    // Show notification
    const showNotification = (message, type = 'info') => {
      notification.value = {
        show: true,
        message,
        type
      }
      
      // Auto hide after 5 seconds
      setTimeout(() => {
        hideNotification()
      }, 5000)
    }

    // Initialize language from localStorage
    onMounted(() => {
      const savedLanguage = localStorage.getItem('hongkai-language')
      if (savedLanguage) {
        languageStore.setLanguage(savedLanguage)
        locale.value = savedLanguage
      }
      
      // Initialize other app settings
      isLoading.value = false
    })

    // Watch for language changes
    languageStore.$subscribe((mutation, state) => {
      locale.value = state.currentLanguage
      localStorage.setItem('hongkai-language', state.currentLanguage)
    })

    return {
      isLoading,
      notification,
      hideNotification
    }
  }
}
</script>

<style>
/* Global app styles */
#app {
  font-family: 'Inter', 'Noto Sans TC', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #0ea5e9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #0284c7;
}

/* Focus styles for accessibility */
button:focus,
input:focus,
textarea:focus,
select:focus {
  outline: 2px solid #0ea5e9;
  outline-offset: 2px;
}

/* Selection color */
::selection {
  background-color: #0ea5e9;
  color: white;
}

::-moz-selection {
  background-color: #0ea5e9;
  color: white;
}
</style>